import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EuroIcon from "@mui/icons-material/Euro";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WeekendIcon from "@mui/icons-material/Weekend";
import CelebrationIcon from "@mui/icons-material/Celebration";
import pricingImage from "../assets/images/weekendopvang.jpg"; // Using an existing image from your assets

// Styled Components
const StyledPricingSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
}));

const StyledPricingSubtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  maxWidth: "800px",
  marginLeft: "auto",
  marginRight: "auto",
}));

const StyledPricingImage = styled('img')<{ $isMobile: boolean }>(({ theme, $isMobile }) => ({
  width: "100%",
  height: $isMobile ? "300px" : "400px",
  objectFit: "cover",
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[3],
}));

const StyledPricingPaper = styled(Paper)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1),
  backgroundColor: $isDarkMode ? "#3D3D3D" : theme.palette.background.paper,
  color: $isDarkMode ? "#F0F0F0" : theme.palette.text.primary,
}));

const StyledPricingListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledNoteBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(0.5),
}));

const StyledLastPricingListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  borderBottom: 'none',
}));

export const PricingSection: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <StyledPricingSection
      id="pricing"
      component="section"
      aria-labelledby="pricing-heading"
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          id="pricing-heading"
          color="primary.main"
        >
          Onze Tarieven
        </Typography>

        <StyledPricingSubtitle
          variant="body1"
          align="center"
        >
          Bij Pippaya bieden we transparante en eerlijke prijzen voor de opvang
          van uw huisdier.
        </StyledPricingSubtitle>

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <StyledPricingImage
              src={pricingImage}
              alt="Huisdier geniet van opvang bij Pippaya"
              $isMobile={isMobile}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledPricingPaper elevation={3} $isDarkMode={isDarkMode}>
              <Typography
                variant="h5"
                component="h3"
                gutterBottom
                color="secondary.main"
              >
                Tarieven Overzicht
              </Typography>

              <List aria-label="Prijslijst voor hondenopvang">
                <StyledPricingListItem>
                  <ListItemIcon>
                    <EuroIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dagopvang"
                    secondary="Inclusief 2 wandelingen"
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €37,50
                  </Typography>
                </StyledPricingListItem>

                <StyledPricingListItem>
                  <ListItemIcon>
                    <WeekendIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="1 tot 5 nachten"
                    secondary="Prijs per nacht voor kortere verblijven"
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €40,00
                  </Typography>
                </StyledPricingListItem>

                <StyledPricingListItem>
                  <ListItemIcon>
                    <CalendarMonthIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="6 nachten of meer"
                    secondary="Prijs per nacht voor langere verblijven"
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €35,00
                  </Typography>
                </StyledPricingListItem>
                <StyledLastPricingListItem>
                  <ListItemIcon>
                    <CelebrationIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Losse feestdag"
                    secondary="Inclusief overnachting indien gewenst"
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €50,00
                  </Typography>
                </StyledLastPricingListItem>
              </List>

              <StyledNoteBox>
                <Typography variant="body2" color="text.secondary">
                  <strong>Opmerking:</strong> Alle prijzen zijn exclusief
                  voeding. <br />
                  <br />
                  Voor speciale dieetwensen of medicatie kunnen extra kosten in
                  rekening worden gebracht. Neem contact met ons op voor een
                  persoonlijk gesprek.
                </Typography>
              </StyledNoteBox>
            </StyledPricingPaper>
          </Grid>
        </Grid>
      </Container>
    </StyledPricingSection>
  );
};
